import type { Role } from '@app/domain/iam/user.interface'
import type { AppAction } from '@app/state'

export type User = {
  userId: string
  email: string
  givenName: string
  familyName: string
  role?: Role
}

export const enum UserActionStatus {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export interface State {
  account?: User
  passwordUpdateStatus?: UserActionStatus
  verifyPinStatus?: UserActionStatus
  clearPatientDataStatus?: UserActionStatus
  errorMessage?: string
}

export enum ActionType {
  GetUser = 'USER/GET_USER',
  GetRole = 'USER/GET_ROLE',
  GetAccountInfo = 'USER/GET_ACCOUNT_INFO',
  GetAccountInfoSucceeded = 'USER/GET_ACCOUNT_INFO/SUCCEEDED',
  GetAccountInfoFailed = 'USER/GET_ACCOUNT_INFO/FAILED',
  UserRetrieved = 'USER/RETRIEVED',
  UpdatePassword = 'USER/UPDATE_PASSWORD',
  PasswordUpdateStarted = 'USER/UPDATE_PASSWORD/Started',
  PasswordUpdateSucceeded = 'USER/UPDATE_PASSWORD/SUCCEEDED',
  PasswordUpdateFailed = 'USER/UPDATE_PASSWORD/FAILED',
  VerifyPIN = 'USER/VERIFY_PIN',
  BeginVerifyPIN = 'USER/VERIFY_PIN/BEGIN',
  VerifyPINSucceeded = 'USER/VERIFY_PIN/SUCCEEDED',
  VerifyPINFailed = 'USER/VERIFY_PIN/FAILED',
  ClearPatientData = 'USER/CLEAR_PATIENT_DATA',
  ClearPatientDataStarted = 'USER/CLEAR_PATIENT_DATA/STARTED',
  ClearPatientDataSuccess = 'USER/CLEAR_PATIENT_DATA/SUCCESS',
  ClearPatientDataFailure = 'USER/CLEAR_PATIENT_DATA/FAILURE',
}

export type Action =
  | { type: ActionType.GetRole; userId: string }
  | { type: ActionType.GetUser; userId: string }
  | { type: ActionType.GetAccountInfo }
  | {
      type: ActionType.GetAccountInfoSucceeded
      userId: string
      email: string
      givenName: string
      familyName: string
      role?: Role
    }
  | { type: ActionType.GetAccountInfoFailed; error: string }
  | { type: ActionType.UserRetrieved; user: User }
  | {
      type: ActionType.UpdatePassword
      identifier: string
      password: string
      newPassword: string
    }
  | { type: ActionType.PasswordUpdateStarted }
  | { type: ActionType.PasswordUpdateSucceeded }
  | { type: ActionType.PasswordUpdateFailed; errorMessage: string }
  | {
      type: ActionType.VerifyPIN
      pin: string
      route: string
      closeSurvey?: boolean
    }
  | { type: ActionType.BeginVerifyPIN }
  | { type: ActionType.VerifyPINSucceeded; route: string }
  | { type: ActionType.VerifyPINFailed; error: string }
  | { type: ActionType.ClearPatientData; passphrase: string; confirm: string }
  | { type: ActionType.ClearPatientDataStarted }
  | { type: ActionType.ClearPatientDataSuccess }
  | { type: ActionType.ClearPatientDataFailure; error: string }

export const actions = {
  getUser(userId: string): Action {
    return { type: ActionType.GetUser, userId }
  },
  getAccountInfo(): Action {
    return { type: ActionType.GetAccountInfo }
  },
  getAccountInfoSucceeded(
    userId: string,
    email: string,
    givenName: string,
    familyName: string,
    role?: Role,
  ): Action {
    return {
      type: ActionType.GetAccountInfoSucceeded,
      userId,
      email,
      givenName,
      familyName,
      role,
    }
  },
  getAccountInfoFailed(error: string): Action {
    return { type: ActionType.GetAccountInfoFailed, error }
  },
  updatePassword(
    identifier: string,
    password: string,
    newPassword: string,
  ): Action {
    return {
      type: ActionType.UpdatePassword,
      identifier,
      password,
      newPassword,
    }
  },
  passwordUpdateStarted(): Action {
    return { type: ActionType.PasswordUpdateStarted }
  },
  passwordUpdateSucceeded(): Action {
    return { type: ActionType.PasswordUpdateSucceeded }
  },
  passwordUpdateFailed(error: string): Action {
    return { type: ActionType.PasswordUpdateFailed, errorMessage: error }
  },
  verifyPIN(pin: string, route: string, closeSurvey?: boolean): Action {
    return { type: ActionType.VerifyPIN, pin, route, closeSurvey }
  },
  beginVerifyPIN(): Action {
    return { type: ActionType.BeginVerifyPIN }
  },
  verifyPINSucceeded(route: string): Action {
    return { type: ActionType.VerifyPINSucceeded, route }
  },
  verifyPINFailed(error: string): Action {
    return { type: ActionType.VerifyPINFailed, error }
  },
  clearPatientData(passphrase: string, confirm: string): Action {
    return { type: ActionType.ClearPatientData, passphrase, confirm }
  },
  clearPatientDataStarted(): Action {
    return { type: ActionType.ClearPatientDataStarted }
  },
  clearPatientDataSuccess(): Action {
    return { type: ActionType.ClearPatientDataSuccess }
  },
  clearPatientDataFailure(error: string): Action {
    return { type: ActionType.ClearPatientDataFailure, error }
  },
}

export const initialState: State = {}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.GetUser:
      return {
        ...state,
      }
    case ActionType.UserRetrieved:
      return {
        ...state,
        account: action.user,
      }
    case ActionType.GetAccountInfoSucceeded:
      return {
        ...state,
        account: {
          userId: action.userId,
          email: action.email,
          givenName: action.givenName,
          familyName: action.familyName,
          role: action.role,
        },
      }
    case ActionType.UpdatePassword:
      return {
        ...state,
      }
    case ActionType.PasswordUpdateStarted:
      return {
        ...state,
        passwordUpdateStatus: UserActionStatus.Pending,
      }
    case ActionType.PasswordUpdateSucceeded:
      return {
        ...state,
        passwordUpdateStatus: UserActionStatus.Succeeded,
      }
    case ActionType.PasswordUpdateFailed:
      return {
        ...state,
        passwordUpdateStatus: UserActionStatus.Failed,
        errorMessage: action.errorMessage,
      }
    case ActionType.BeginVerifyPIN:
      return {
        ...state,
        verifyPinStatus: UserActionStatus.Pending,
      }
    case ActionType.VerifyPINFailed:
      return {
        ...state,
        verifyPinStatus: UserActionStatus.Failed,
      }
    case ActionType.VerifyPINSucceeded:
      return {
        ...state,
        verifyPinStatus: UserActionStatus.Succeeded,
      }
    case ActionType.ClearPatientDataStarted:
      return {
        ...state,
        clearPatientDataStatus: UserActionStatus.Pending,
      }
    case ActionType.ClearPatientDataSuccess:
      return {
        ...state,
        clearPatientDataStatus: UserActionStatus.Succeeded,
      }
    case ActionType.ClearPatientDataFailure:
      return {
        ...state,
        clearPatientDataStatus: UserActionStatus.Failed,
        errorMessage: action.error,
      }
    default:
      return state
  }
}
